import React, { useEffect, useMemo, useState } from "react";
import ProcedureDiagnosisService from "./procedureDiagnosisService";
import "./ProcedureDiagnosisManager.css";

const procedureService = new ProcedureDiagnosisService();

const ProcedureDiagnosisManager = () => {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    code: "",
    description: "",
    differenceCode: "P",
    picture: "",
  });

  const role = localStorage.getItem("role");

  const canManage = role === "ADMIN" || role === "MANAGER";

  useEffect(() => {
    if (!canManage) {
      setMessage("You do not have permission to manage procedure/diagnosis codes.");
      return;
    }

    fetchItems();
  }, [canManage]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      setMessage("");

      const data = await procedureService.getAllProcedures();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load procedure/diagnosis codes:", err);
      setMessage("Failed to load procedure/diagnosis codes.");
    } finally {
      setLoading(false);
    }
  };

  const getId = (item) => {
    return item.procedure_id ?? item.procedureId ?? item.id;
  };

  const getTypeLabel = (differenceCode) => {
    const code = differenceCode?.toUpperCase();

    if (code === "P") return "Procedure";
    if (code === "D") return "Diagnosis";

    return "Unknown";
  };

  const filteredItems = useMemo(() => {
    const search = query.trim().toLowerCase();

    return items.filter((item) => {
      const code = item.code?.toLowerCase() || "";
      const description = item.description?.toLowerCase() || "";
      const differenceCode = item.differenceCode?.toUpperCase() || "";

      const matchesSearch =
        code.includes(search) ||
        description.includes(search) ||
        differenceCode.toLowerCase().includes(search);

      const matchesType =
        typeFilter === "ALL" || differenceCode === typeFilter;

      return matchesSearch && matchesType;
    });
  }, [items, query, typeFilter]);

  const startEdit = (item) => {
    const id = getId(item);

    setEditingId(id);
    setEditForm({
      code: item.code || "",
      description: item.description || "",
      differenceCode: item.differenceCode || "P",
      picture: item.picture || "",
    });

    setMessage("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({
      code: "",
      description: "",
      differenceCode: "P",
      picture: "",
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;

    setEditForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const saveEdit = async (id) => {
    if (!editForm.code.trim()) {
      setMessage("Code is required.");
      return;
    }

    if (!editForm.description.trim()) {
      setMessage("Description is required.");
      return;
    }

    if (!editForm.differenceCode) {
      setMessage("Type is required.");
      return;
    }

    try {
      setLoading(true);
      setMessage("");

      const payload = {
        code: editForm.code.trim(),
        description: editForm.description.trim(),
        differenceCode: editForm.differenceCode,
        picture: editForm.picture || null,
      };

      await procedureService.updateProcedure(id, payload);

      setItems((prev) =>
        prev.map((item) => {
          const itemId = getId(item);

          if (itemId !== id) return item;

          return {
            ...item,
            ...payload,
          };
        })
      );

      cancelEdit();
      setMessage("Code updated successfully.");
    } catch (err) {
      console.error("Failed to update code:", err);
      setMessage("Failed to update code.");
    } finally {
      setLoading(false);
    }
  };

  const deleteItem = async (item) => {
    const id = getId(item);

    if (!id) {
      setMessage("Could not find ID for this code.");
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${item.code}? This cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      setLoading(true);
      setMessage("");

      await procedureService.deleteProcedure(id);

      setItems((prev) => prev.filter((existing) => getId(existing) !== id));

      if (editingId === id) {
        cancelEdit();
      }

      setMessage("Code deleted successfully.");
    } catch (err) {
      console.error("Failed to delete code:", err);
      setMessage("Failed to delete code. It may be connected to other records.");
    } finally {
      setLoading(false);
    }
  };

  if (!canManage) {
    return (
      <div className="pd-manager-wrapper">
        <div className="pd-manager-card">
          <h2>Procedure / Diagnosis Manager</h2>
          <p className="pd-error">
            You do not have permission to view this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pd-manager-wrapper">
      <div className="pd-manager-card">
        <div className="pd-manager-header">
          <div>
            <h2>Procedure / Diagnosis Manager</h2>
            <p>
              Search, edit, and delete procedure and diagnosis codes.
            </p>
          </div>

          <button
            className="pd-refresh-button"
            onClick={fetchItems}
            disabled={loading}
          >
            Refresh
          </button>
        </div>

        {message && <div className="pd-message">{message}</div>}

        <div className="pd-controls">
          <input
            type="text"
            className="pd-search-input"
            placeholder="Search by code, description, or type..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />

          <select
            className="pd-type-filter"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="ALL">All</option>
            <option value="P">Procedures</option>
            <option value="D">Diagnoses</option>
          </select>
        </div>

        <div className="pd-count-row">
          Showing {filteredItems.length} of {items.length}
        </div>

        {loading && <p className="pd-loading">Loading...</p>}

        <div className="pd-table-wrapper">
          <table className="pd-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Description</th>
                <th>Type</th>
                <th>Picture</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {!loading && filteredItems.length === 0 && (
                <tr>
                  <td colSpan="5" className="pd-empty">
                    No results found.
                  </td>
                </tr>
              )}

              {filteredItems.map((item) => {
                const id = getId(item);
                const isEditing = editingId === id;

                return (
                  <tr key={id || item.code}>
                    <td>
                      {isEditing ? (
                        <input
                          name="code"
                          className="pd-edit-input"
                          value={editForm.code}
                          onChange={handleEditChange}
                        />
                      ) : (
                        item.code
                      )}
                    </td>

                    <td>
                      {isEditing ? (
                        <textarea
                          name="description"
                          className="pd-edit-textarea"
                          value={editForm.description}
                          onChange={handleEditChange}
                        />
                      ) : (
                        item.description
                      )}
                    </td>

                    <td>
                      {isEditing ? (
                        <select
                          name="differenceCode"
                          className="pd-edit-select"
                          value={editForm.differenceCode}
                          onChange={handleEditChange}
                        >
                          <option value="P">Procedure</option>
                          <option value="D">Diagnosis</option>
                        </select>
                      ) : (
                        <span
                          className={
                            item.differenceCode?.toUpperCase() === "D"
                              ? "pd-badge diagnosis"
                              : "pd-badge procedure"
                          }
                        >
                          {getTypeLabel(item.differenceCode)}
                        </span>
                      )}
                    </td>

                    <td>
                      {isEditing ? (
                        <input
                          name="picture"
                          className="pd-edit-input"
                          value={editForm.picture}
                          onChange={handleEditChange}
                          placeholder="Optional picture URL"
                        />
                      ) : item.picture ? (
                        <span className="pd-picture-text">{item.picture}</span>
                      ) : (
                        <span className="pd-muted">None</span>
                      )}
                    </td>

                    <td>
                      {isEditing ? (
                        <div className="pd-action-group">
                          <button
                            className="pd-save-button"
                            onClick={() => saveEdit(id)}
                            disabled={loading}
                          >
                            Save
                          </button>

                          <button
                            className="pd-cancel-button"
                            onClick={cancelEdit}
                            disabled={loading}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="pd-action-group">
                          <button
                            className="pd-edit-button"
                            onClick={() => startEdit(item)}
                            disabled={loading}
                          >
                            Edit
                          </button>

                          <button
                            className="pd-delete-button"
                            onClick={() => deleteItem(item)}
                            disabled={loading}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProcedureDiagnosisManager;